/**
 * CommandPalette — portal-wide search overlay opened from the TopBar search
 * field (click) or ⌘K / Ctrl+K on web. Searches three sources:
 *   • Sections — nav items (src/app/nav.ts), filtered locally by label.
 *   • People + Posts — one live `/api/search?q=` call (debounced, ≥ 2 chars),
 *     which replaces the old 2-request fan-out (people + first feed page).
 * Keyboard: ↑/↓ move, ↵ open, esc close (web). Selecting a section routes to it;
 * a person routes to /people; a news item routes to /post/[id].
 * Chrome follows the `SearchPalette` design in design_handoff_as_life_portal.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { useRouter } from 'expo-router'
import { useQuery } from '@tanstack/react-query'
import { Input, ScrollView, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../i18n'
import { useResponsive } from '../lib/responsive'
import { api } from '../api/client'
import { timeAgo } from '../lib/format'
import { Avatar, Icon } from '../ui'
import { palette } from '../theme/palette'
import { isComingSoon, type NavRoute } from './nav'

/**
 * Curated "Разделы" shortcuts, matching the handoff SearchPalette (max 4).
 * Tasks + requests are one destination (/processes), so they're a single
 * «Процессы» entry here — mirrors the sidebar nav (`nav.processes`).
 */
const ALL_SECTIONS: { key: string; icon: string; route: NavRoute }[] = [
  { key: 'processes', icon: 'file-text', route: '/processes' },
  { key: 'kb', icon: 'book-marked', route: '/kb' },
  { key: 'people', icon: 'users', route: '/people' },
]
// drop not-ready sections (e.g. /learning) — separate `const` so the array literal above
// keeps its NavRoute contextual typing (lost if `.filter()` chained straight off it).
const SECTIONS = ALL_SECTIONS.filter((s) => !isComingSoon(s.route))

type CmdItem =
  | { kind: 'page'; route: NavRoute; icon: string; label: string }
  | { kind: 'person'; id: string; name: string; sub: string }
  | { kind: 'news'; id: string; title: string; sub: string }

const isWeb = Platform.OS === 'web'

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { l } = useApp()
  const { isPhone } = useResponsive()
  const router = useRouter()

  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const [debounced, setDebounced] = useState('')

  // Reset query each time the palette opens.
  useEffect(() => {
    if (open) {
      setQ('')
      setActive(0)
    }
  }, [open])

  // Debounce the people query so we don't hit the API on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 200)
    return () => clearTimeout(id)
  }, [q])

  // Unified search: one call → { people, posts }, both filtered server-side by q.
  const { data: results } = useQuery({
    queryKey: ['cmd-search', debounced],
    queryFn: ({ signal }) => api.search(debounced, { signal }),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  })

  const term = q.trim().toLowerCase()

  const pageItems = useMemo<CmdItem[]>(
    () =>
      SECTIONS.filter((n) => l(`nav.${n.key}`).toLowerCase().includes(term))
        .slice(0, 4)
        .map((n) => ({
          kind: 'page',
          route: n.route,
          icon: n.icon,
          label: l(`nav.${n.key}`),
        })),
    [term, l],
  )

  const personItems = useMemo<CmdItem[]>(
    () =>
      (results?.people ?? []).slice(0, 8).map((u) => ({
        kind: 'person',
        id: u.id,
        name: u.fullName,
        sub: u.jobTitle ?? u.department ?? '',
      })),
    [results],
  )

  const newsItems = useMemo<CmdItem[]>(
    () =>
      (results?.posts ?? []).slice(0, 4).map((p) => ({
        kind: 'news',
        id: p.id,
        title: p.title,
        sub: p.createdAt ? timeAgo(p.createdAt) : '',
      })),
    [results],
  )

  const items = useMemo<CmdItem[]>(
    () => [...pageItems, ...personItems, ...newsItems],
    [pageItems, personItems, newsItems],
  )

  // Clamp the active index whenever results change.
  useEffect(() => {
    setActive((a) => (a >= items.length ? 0 : a))
  }, [items.length])

  const select = (it: CmdItem) => {
    onClose()
    if (it.kind === 'page') router.push(it.route)
    else if (it.kind === 'news') router.push({ pathname: '/post/[id]', params: { id: it.id } })
    else router.push('/people')
  }

  // Keep handler refs fresh so the web key listener stays registered on `open`.
  const itemsRef = useRef(items)
  itemsRef.current = items
  const activeRef = useRef(active)
  activeRef.current = active
  const selectRef = useRef(select)
  selectRef.current = select

  useEffect(() => {
    if (!open || !isWeb) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive((a) => Math.min(a + 1, Math.max(itemsRef.current.length - 1, 0)))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive((a) => Math.max(a - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const it = itemsRef.current[activeRef.current]
        if (it) selectRef.current(it)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const Row = ({ it, idx }: { it: CmdItem; idx: number }) => {
    const on = idx === active
    const title = it.kind === 'person' ? it.name : it.kind === 'news' ? it.title : it.label
    const sub = it.kind === 'page' ? undefined : it.sub
    return (
      <XStack
        onPress={() => select(it)}
        onHoverIn={() => setActive(idx)}
        cursor="pointer"
        alignItems="center"
        gap={12}
        paddingHorizontal={18}
        paddingVertical={9}
        backgroundColor={on ? '$bgLayout' : 'transparent'}
      >
        {it.kind === 'person' ? (
          <Avatar name={it.name} seed={it.id} size="sm" />
        ) : (
          <Stack
            width={28}
            height={28}
            borderRadius={8}
            backgroundColor="$bgLayout"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon name={it.kind === 'news' ? 'newspaper' : it.icon} size={15} color="$muted" />
          </Stack>
        )}
        <YStack flex={1} minWidth={0}>
          <Text fontSize={14} fontWeight="500" color="$color" numberOfLines={1}>
            {title}
          </Text>
          {!!sub && (
            <Text fontSize={11} color="$tertiary" numberOfLines={1}>
              {sub}
            </Text>
          )}
        </YStack>
        <Icon name="corner-down-left" size={13} color="$tertiary" />
      </XStack>
    )
  }

  const SectionHeader = ({ label }: { label: string }) => (
    <Text
      fontSize={11}
      fontWeight="700"
      letterSpacing={0.6}
      color="$tertiary"
      paddingHorizontal={18}
      paddingTop={12}
      paddingBottom={6}
    >
      {label.toUpperCase()}
    </Text>
  )

  return (
    // `fixed` on web so the scrim covers the sidebar and the panel centers on the
    // whole viewport (not just the content column the TopBar lives in).
    <YStack position={isWeb ? ('fixed' as 'absolute') : 'absolute'} top={0} left={0} right={0} bottom={0} zIndex={200}>
      {/* backdrop */}
      <Stack
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor={palette.scrim}
        onPress={onClose}
        animation="quicker"
        enterStyle={{ opacity: 0 }}
        opacity={1}
      />

      {/* panel */}
      <YStack
        animation="quicker"
        enterStyle={{ opacity: 0, scale: 0.96, y: -10 }}
        opacity={1}
        scale={1}
        y={0}
        alignSelf="center"
        marginTop={isPhone ? 0 : 96}
        width={isPhone ? '100%' : 560}
        maxWidth="100%"
        height={isPhone ? '100%' : undefined}
        maxHeight={isPhone ? '100%' : 520}
        backgroundColor="$bgElevated"
        borderWidth={1}
        borderColor="$borderSecondary"
        borderRadius={isPhone ? 0 : 12}
        overflow="hidden"
        shadowColor={palette.shadow}
        shadowOpacity={0.22}
        shadowRadius={28}
        shadowOffset={{ width: 0, height: 16 }}
      >
        {/* search field */}
        <XStack alignItems="center" gap={12} paddingHorizontal={18} height={54} borderBottomWidth={1} borderColor="$borderSecondary">
          <Icon name="search" size={18} color="$tertiary" />
          <Input
            unstyled
            flex={1}
            height={50}
            fontSize={16}
            color="$color"
            borderWidth={0}
            outlineWidth={0}
            outlineColor="transparent"
            outlineStyle="none"
            backgroundColor="transparent"
            placeholder={l('top.search')}
            placeholderTextColor="$tertiary"
            value={q}
            onChangeText={setQ}
            focusStyle={{ outlineWidth: 0, borderWidth: 0 }}
            autoFocus
          />
          {isPhone ? (
            <Stack onPress={onClose} cursor="pointer" paddingHorizontal={6} paddingVertical={3} borderRadius={5} hoverStyle={{ backgroundColor: '$backgroundHover' }}>
              <Icon name="x" size={18} color="$muted" />
            </Stack>
          ) : (
            <Stack
              onPress={onClose}
              cursor="pointer"
              borderWidth={1}
              borderColor="$borderSecondary"
              borderRadius={4}
              paddingHorizontal={6}
              paddingVertical={2}
              hoverStyle={{ backgroundColor: '$backgroundHover' }}
            >
              <Text fontSize={11} color="$tertiary">
                esc
              </Text>
            </Stack>
          )}
        </XStack>

        {/* results */}
        <ScrollView maxHeight={isPhone ? undefined : 420} flex={isPhone ? 1 : undefined} keyboardShouldPersistTaps="handled">
          {pageItems.length > 0 && <SectionHeader label={l('cmd.sections')} />}
          {pageItems.map((it, idx) => (
            <Row key={`p-${it.kind === 'page' ? it.route : idx}`} it={it} idx={idx} />
          ))}

          {personItems.length > 0 && <SectionHeader label={l('cmd.people')} />}
          {personItems.map((it, i) => (
            <Row key={`u-${it.kind === 'person' ? it.id : i}`} it={it} idx={pageItems.length + i} />
          ))}

          {newsItems.length > 0 && <SectionHeader label={l('nav.news')} />}
          {newsItems.map((it, i) => (
            <Row key={`n-${it.kind === 'news' ? it.id : i}`} it={it} idx={pageItems.length + personItems.length + i} />
          ))}

          {items.length === 0 && (
            <YStack alignItems="center" paddingVertical={40} gap={8}>
              <Icon name="search" size={22} color="$tertiary" />
              <Text fontSize={13} color="$muted">
                {l('cmd.empty')}
              </Text>
            </YStack>
          )}
        </ScrollView>
      </YStack>
    </YStack>
  )
}
